import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getPayrollReports,
  clearError,
  clearReports,
} from "../../../../redux/Slices/authSlice";
import { FaCalendarAlt, FaFileExcel, FaPrint, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import SortOrderSelect from "../../../../shared/components/SortOrderSelect";
import {
  chunkArray,
  convertArabicNumbersToEnglish,
} from "../../../../shared/utils.js/utils";

const PayrollReport = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { payrollReports, isLoading, error } = useSelector(state => state.auth);
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const locale = "en-US";
  const [reportDate, setReportDate] = useState("");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isFetchingReport, setIsFetchingReport] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [allReports, setAllReports] = useState([]);
  const pdfContainerRef = useRef(null);
  const singleEmployeePdfRef = useRef(null);
  const [pdfChunks, setPdfChunks] = useState([]);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isGeneratingSinglePdf, setIsGeneratingSinglePdf] = useState(false);
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("employee_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const formatNumberEnDigits = (value, intlOptions) =>
    convertArabicNumbersToEnglish(
      String(parseFloat(value ?? 0).toLocaleString(locale, intlOptions)),
    );

  const formatReportMonthYear = () => {
    if (!reportDate) return "";
    const s = new Date(`${reportDate}-01`).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
    });
    return convertArabicNumbersToEnglish(String(s));
  };

  useEffect(() => {
    setTitle("sidenav.reports.payroll");
  }, [setTitle]);

  useEffect(() => {
    return () => {
      setReportDate("");
      dispatch(clearReports());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  // Update allReports when payrollReports changes
  useEffect(() => {
    if (payrollReports?.data && Array.isArray(payrollReports.data)) {
      setAllReports(payrollReports.data);
      setCurrentPage(1);
    } else {
      setAllReports([]);
    }
  }, [payrollReports]);

  // Unique departments computed from reports data
  const uniqueDepartments = React.useMemo(() => {
    const list = allReports
      .map(
        item =>
          item.department ||
          (item.employee?.department
            ? (isRTL
                ? item.employee.department.name_ar
                : item.employee.department.name_en) ||
              item.employee.department.name
            : ""),
      )
      .filter(Boolean);
    return Array.from(new Set(list));
  }, [allReports, isRTL]);

  // Client-side filtering and sorting
  const filteredAndSortedReports = React.useMemo(() => {
    let result = [...allReports];

    // search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(item => {
        const empName =
          item.employee_name ||
          (item.employee
            ? (isRTL ? item.employee.name_ar : item.employee.name_en) ||
              item.employee.name
            : "");
        return empName.toLowerCase().includes(term);
      });
    }

    // dept filter
    if (deptFilter !== "all") {
      result = result.filter(item => {
        const dept =
          item.department ||
          (item.employee?.department
            ? (isRTL
                ? item.employee.department.name_ar
                : item.employee.department.name_en) ||
              item.employee.department.name
            : "");
        return dept === deptFilter;
      });
    }

    // status filter
    if (statusFilter !== "all") {
      result = result.filter(item => {
        const status = item.status || "";
        return status === statusFilter;
      });
    }

    // sort
    result.sort((a, b) => {
      let aVal = "";
      let bVal = "";

      if (sortField === "employee_name") {
        aVal =
          a.employee_name ||
          (a.employee
            ? (isRTL ? a.employee.name_ar : a.employee.name_en) ||
              a.employee.name
            : "");
        bVal =
          b.employee_name ||
          (b.employee
            ? (isRTL ? b.employee.name_ar : b.employee.name_en) ||
              b.employee.name
            : "");
      } else if (sortField === "department") {
        aVal =
          a.department ||
          (a.employee?.department
            ? (isRTL
                ? a.employee.department.name_ar
                : a.employee.department.name_en) || a.employee.department.name
            : "");
        bVal =
          b.department ||
          (b.employee?.department
            ? (isRTL
                ? b.employee.department.name_ar
                : b.employee.department.name_en) || b.employee.department.name
            : "");
      } else if (sortField === "status") {
        aVal = a.status || "";
        bVal = b.status || "";
      } else {
        // numeric fields
        aVal = parseFloat(a[sortField]) || 0;
        bVal = parseFloat(b[sortField]) || 0;
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal), isRTL ? "ar" : "en")
        : String(bVal).localeCompare(String(aVal), isRTL ? "ar" : "en");
    });

    return result;
  }, [
    allReports,
    searchTerm,
    deptFilter,
    statusFilter,
    sortField,
    sortOrder,
    isRTL,
  ]);

  // Height-based dynamic PDF chunking
  const updatePdfChunks = () => {
    if (!filteredAndSortedReports || filteredAndSortedReports.length === 0) {
      setPdfChunks([]);
      return;
    }

    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.top = "-99999px";
    container.style.left = "-99999px";
    container.style.width = "794px";
    container.style.visibility = "hidden";
    document.body.appendChild(container);

    const testPage = document.createElement("div");
    testPage.style.width = "794px";
    testPage.style.height = "1123px";
    testPage.style.padding = "40px";
    testPage.style.boxSizing = "border-box";
    testPage.style.border = "5px solid #000";

    const pageBorder = document.createElement("div");
    pageBorder.style.border = "2px solid #000";
    pageBorder.style.height = "1033px";
    pageBorder.style.padding = "18px 18px 10px";
    pageBorder.style.display = "flex";
    pageBorder.style.flexDirection = "column";
    pageBorder.style.boxSizing = "border-box";

    testPage.appendChild(pageBorder);
    container.appendChild(testPage);

    const topHeader = document.getElementById("pdf-measure-top-header");
    let topHeaderHeight = 220;
    if (topHeader) {
      const clonedHeader = topHeader.cloneNode(true);
      pageBorder.appendChild(clonedHeader);
      topHeaderHeight = clonedHeader.offsetHeight;
      pageBorder.removeChild(clonedHeader);
    }

    const tableHeader = document.getElementById("pdf-measure-thead");
    let tableHeaderHeight = 35;
    if (tableHeader) {
      const tableWrapper = document.createElement("table");
      tableWrapper.style.width = "100%";
      tableWrapper.style.borderCollapse = "collapse";
      const clonedTHead = tableHeader.cloneNode(true);
      tableWrapper.appendChild(clonedTHead);
      pageBorder.appendChild(tableWrapper);
      tableHeaderHeight = tableWrapper.offsetHeight;
      pageBorder.removeChild(tableWrapper);
    }

    const footer = document.getElementById("pdf-measure-footer");
    let footerHeight = 70;
    if (footer) {
      const clonedFooter = footer.cloneNode(true);
      pageBorder.appendChild(clonedFooter);
      footerHeight = clonedFooter.offsetHeight;
      pageBorder.removeChild(clonedFooter);
    }

    document.body.removeChild(container);

    const PAGE_MAX_HEIGHT = 1033;
    const availableHeightForTable =
      PAGE_MAX_HEIGHT - topHeaderHeight - footerHeight - 15;

    const tbody = document.getElementById("pdf-measure-tbody");
    let rowHeights = [];
    if (tbody) {
      const rows = tbody.querySelectorAll("tr");
      rowHeights = Array.from(rows).map(row => row.offsetHeight || 30);
    } else {
      rowHeights = filteredAndSortedReports.map(() => 30);
    }

    const chunks = [];
    let currentChunk = [];
    let currentHeight = tableHeaderHeight;

    for (let i = 0; i < filteredAndSortedReports.length; i++) {
      const rHeight = rowHeights[i] || 30;
      if (currentHeight + rHeight > availableHeightForTable) {
        chunks.push(currentChunk);
        currentChunk = [filteredAndSortedReports[i]];
        currentHeight = tableHeaderHeight + rHeight;
      } else {
        currentChunk.push(filteredAndSortedReports[i]);
        currentHeight += rHeight;
      }
    }
    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    setPdfChunks(chunks);
  };

  useEffect(() => {
    if (filteredAndSortedReports && filteredAndSortedReports.length > 0) {
      const timer = setTimeout(() => {
        updatePdfChunks();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setPdfChunks([]);
    }
  }, [filteredAndSortedReports]);

  const handleGenerateReport = async () => {
    if (!reportDate) {
      toast.error(t("reports.payroll.selectMonth"), { rtl: isRTL });
      return;
    }
    const [year, month] = reportDate.split("-");
    setIsFetchingReport(true);
    try {
      await dispatch(getPayrollReports({ year: year, month: month }));
    } finally {
      setIsFetchingReport(false);
    }
  };

  const handleApplyPerPage = () => {
    const validValue = Math.max(
      1,
      Math.min(perPageInput, filteredAndSortedReports?.length || 1000),
    );
    setPerPage(validValue);
    setCurrentPage(1);
  };

  // Client-side pagination
  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSortedReports.length / perPage),
  );
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedReports = filteredAndSortedReports.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage,
  );

  const handleExportToExcel = () => {
    if (!filteredAndSortedReports || filteredAndSortedReports.length === 0) {
      toast.error(t("reports.payroll.noDataToExport"), { rtl: isRTL });
      return;
    }

    const data = filteredAndSortedReports.map((item, index) => ({
      [t("reports.payroll.excel.serial")]: index + 1,
      [t("reports.payroll.excel.employee")]:
        item.employee_name ||
        (item.employee
          ? (isRTL ? item.employee.name_ar : item.employee.name_en) ||
            item.employee.name
          : "-"),
      [t("reports.payroll.excel.department")]:
        item.department ||
        (item.employee?.department
          ? (isRTL
              ? item.employee.department.name_ar
              : item.employee.department.name_en) ||
            item.employee.department.name
          : "-"),
      [t("reports.payroll.excel.baseSalary")]: parseFloat(
        item.base_salary || 0,
      ),
      [t("reports.payroll.excel.allowances")]: parseFloat(
        item.total_allowances || 0,
      ),
      [t("reports.payroll.excel.deductions")]: parseFloat(
        item.total_deductions || 0,
      ),
      [t("reports.payroll.excel.netSalary")]: parseFloat(item.net_salary || 0),
      [t("reports.payroll.excel.status")]:
        item.status === "generated"
          ? t("reports.payroll.status.generated")
          : item.status || t("reports.payroll.status.unknown"),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("reports.payroll.excel.sheetName"));
    XLSX.writeFile(
      wb,
      `${t("reports.payroll.excel.fileName")}_${reportDate}.xlsx`,
    );
    toast.success(t("reports.payroll.exportSuccess"), { rtl: isRTL });
  };

  const handleExportSingleEmployeeToExcel = employee => {
    if (!employee) {
      toast.error(t("reports.payroll.noDataToExport"), { rtl: isRTL });
      return;
    }

    const data = [
      {
        [t("reports.payroll.excel.employee")]:
          employee.employee_name ||
          (employee.employee
            ? (isRTL ? employee.employee.name_ar : employee.employee.name_en) ||
              employee.employee.name
            : "-"),
        [t("reports.payroll.excel.department")]:
          employee.department ||
          (employee.employee?.department
            ? (isRTL
                ? employee.employee.department.name_ar
                : employee.employee.department.name_en) ||
              employee.employee.department.name
            : "-"),
        [t("reports.payroll.excel.baseSalary")]: parseFloat(
          employee.base_salary,
        ),
        [t("reports.payroll.excel.allowances")]: parseFloat(
          employee.total_allowances,
        ),
        [t("reports.payroll.excel.deductions")]: parseFloat(
          employee.total_deductions,
        ),
        [t("reports.payroll.excel.netSalary")]: parseFloat(employee.net_salary),
        [t("reports.payroll.excel.status")]:
          employee.status === "generated"
            ? t("reports.payroll.status.generated")
            : employee.status || t("reports.payroll.status.unknown"),
      },
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("reports.payroll.excel.sheetName"));
    const employeeName =
      employee.employee_name ||
      (employee.employee
        ? (isRTL ? employee.employee.name_ar : employee.employee.name_en) ||
          employee.employee.name
        : "Employee");
    XLSX.writeFile(
      wb,
      `${t("reports.payroll.excel.fileName")}_${employeeName}_${reportDate}.xlsx`,
    );
    toast.success(t("reports.payroll.exportSuccess"), { rtl: isRTL });
  };

  const handlePrintSingleEmployeePdf = async employee => {
    if (!employee) {
      toast.error(t("reports.payroll.print.noData"), { rtl: isRTL });
      return;
    }
    try {
      setSelectedEmployee(employee);
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsGeneratingSinglePdf(true);

      const element = singleEmployeePdfRef.current;
      if (!element) {
        toast.error(t("reports.payroll.print.templateNotAvailable"), {
          rtl: isRTL,
        });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.getElementById(
            "single-employee-payroll-pdf",
          );
          if (clonedElement) {
            clonedElement.style.fontFamily =
              '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = "bold";
            clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            clonedElement.style.setProperty(
              "-webkit-font-smoothing",
              "antialiased",
            );
            clonedElement.style.setProperty(
              "-moz-osx-font-smoothing",
              "grayscale",
            );
            clonedElement.style.textRendering = "optimizeLegibility";
            clonedElement.style.height = "1123px";
            clonedElement.style.width = "794px";
          }
        },
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(
        imgData,
        "PNG",
        0,
        0,
        pdfWidth,
        pdfHeight,
        undefined,
        "FAST",
      );
      const employeeName =
        employee.employee_name ||
        (employee.employee
          ? (isRTL ? employee.employee.name_ar : employee.employee.name_en) ||
            employee.employee.name
          : "Employee");
      pdf.save(`ACE_Payroll_Report_${employeeName}_${reportDate}.pdf`);
    } catch (error) {
      console.error("Single Employee Payroll Report PDF error:", error);
      toast.error(t("reports.payroll.print.error"), { rtl: isRTL });
    } finally {
      setIsGeneratingSinglePdf(false);
      setSelectedEmployee(null);
    }
  };

  const formatPrintCurrency = amount => {
    const formatted = Number(amount || 0).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return (
      convertArabicNumbersToEnglish(formatted) +
      " " +
      t("reports.payroll.currency")
    );
  };

  const handlePrintPdf = async () => {
    if (!filteredAndSortedReports || filteredAndSortedReports.length === 0) {
      toast.error(t("reports.payroll.print.noData"), { rtl: isRTL });
      return;
    }
    try {
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < pdfChunks.length; i++) {
        const element = document.getElementById(
          `payroll-report-pdf-chunk-${i}`,
        );
        if (!element) continue;

        const canvas = await html2canvas(element, {
          scale: 3,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: true,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `payroll-report-pdf-chunk-${i}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
              clonedElement.style.setProperty(
                "-webkit-font-smoothing",
                "antialiased",
              );
              clonedElement.style.setProperty(
                "-moz-osx-font-smoothing",
                "grayscale",
              );
              clonedElement.style.textRendering = "optimizeLegibility";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        if (i > 0) pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          pdfHeight,
          undefined,
          "FAST",
        );
      }

      pdf.save(`ACE_Payroll_Report_${reportDate}.pdf`);
    } catch (error) {
      console.error("Payroll Report PDF error:", error);
      toast.error(t("reports.payroll.print.error"), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      {/* Title */}
      <div style={{ marginBottom: "30px" }}>
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "white",
            marginBottom: "20px",
            textAlign,
          }}
        >
          {t("reports.payroll.title")}
        </h1>
      </div>

      {/* Date Selection and Main Actions */}
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "20px",
          border: "1px solid #333",
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "15px",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: "white",
                fontSize: "14px",
                fontWeight: "500",
                textAlign,
                direction: dir,
              }}
            >
              {t("reports.payroll.monthYear")}
            </label>
            <input
              type="month"
              value={reportDate}
              onChange={e => {
                setReportDate(e.target.value);
                setAllReports([]);
                dispatch(clearReports());
              }}
              className="month-input-white"
              style={{
                width: "100%",
                padding: "12px 15px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                direction: dir,
                textAlign,
              }}
            />
          </div>
          <button
            onClick={handleGenerateReport}
            disabled={isFetchingReport || isLoading}
            style={{
              backgroundColor:
                isFetchingReport || isLoading ? "#666" : "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              cursor: isFetchingReport || isLoading ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "bold",
              opacity: isFetchingReport || isLoading ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaCalendarAlt />
            {isFetchingReport || isLoading
              ? t("reports.payroll.generating")
              : t("reports.payroll.showReport")}
          </button>
          {filteredAndSortedReports && filteredAndSortedReports.length > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <label
                  style={{
                    color: "white",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("common.perPage", { defaultValue: "عدد السجلات" })}:
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <button
                    onClick={() =>
                      setPerPageInput(Math.max(1, perPageInput - 1))
                    }
                    style={{
                      backgroundColor: "#1b3350",
                      color: "white",
                      border: "1px solid #333",
                      padding: "6px 10px",
                      borderRadius: "6px 0 0 6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "32px",
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={filteredAndSortedReports?.length || 1000}
                    value={perPageInput}
                    onChange={e =>
                      setPerPageInput(
                        Math.max(1, parseInt(e.target.value) || 1),
                      )
                    }
                    style={{
                      width: "70px",
                      padding: "6px 10px",
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderLeft: "none",
                      borderRight: "none",
                      borderRadius: 0,
                      color: "white",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  />
                  <button
                    onClick={() =>
                      setPerPageInput(
                        Math.min(
                          filteredAndSortedReports?.length || 1000,
                          perPageInput + 1,
                        ),
                      )
                    }
                    style={{
                      backgroundColor: "#1b3350",
                      color: "white",
                      border: "1px solid #333",
                      padding: "6px 10px",
                      borderRadius: "0 6px 6px 0",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "32px",
                    }}
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={handleApplyPerPage}
                  style={{
                    backgroundColor: "#AC2000",
                    color: "white",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("common.apply", { defaultValue: "تطبيق" })}
                </button>
              </div>
              <button
                onClick={() => setIsPreviewOpen(true)}
                style={{
                  backgroundColor: "#444",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexDirection: isRTL ? "row" : "row-reverse",
                }}
              >
                <FaEye />
                {t("reports.payroll.preview")}
              </button>
              <button
                onClick={handlePrintPdf}
                disabled={isGeneratingPdf}
                style={{
                  backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  opacity: isGeneratingPdf ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexDirection: isRTL ? "row" : "row-reverse",
                }}
              >
                <FaPrint />
                {isGeneratingPdf
                  ? t("reports.payroll.print.generating")
                  : t("common.print", {
                      defaultValue: isRTL ? "طباعة PDF" : "Print PDF",
                    })}
              </button>
              <button
                onClick={handleExportToExcel}
                style={{
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexDirection: isRTL ? "row" : "row-reverse",
                }}
              >
                <FaFileExcel />
                {t("reports.payroll.exportExcel")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Results View */}
      {allReports && allReports.length > 0 && (
        <div
          style={{
            backgroundColor: "#1b3350",
            borderRadius: "12px",
            padding: "20px",
            border: "1px solid #333",
          }}
        >
          {/* Search, Filters, and Sorting Controls */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "15px",
              marginBottom: "20px",
              alignItems: "flex-end",
              borderBottom: "1px solid #333",
              paddingBottom: "20px",
            }}
          >
            {/* Search Input */}
            <div
              style={{
                flex: "1",
                minWidth: "200px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{ color: "white", fontSize: "13px", direction: dir }}
              >
                {t("common.search", { defaultValue: isRTL ? "بحث" : "Search" })}
              </label>
              <input
                type="text"
                placeholder={t("reports.payroll.searchPlaceholder", {
                  defaultValue: isRTL ? "بحث عن موظف..." : "Search employee...",
                })}
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  direction: dir,
                  textAlign,
                }}
              />
            </div>

            {/* Department Filter */}
            <div
              style={{
                minWidth: "180px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{ color: "white", fontSize: "13px", direction: dir }}
              >
                {t("reports.payroll.table.department")}
              </label>
              <select
                value={deptFilter}
                onChange={e => {
                  setDeptFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  direction: dir,
                }}
              >
                <option value="all">
                  {t("common.all", { defaultValue: isRTL ? "الكل" : "All" })}
                </option>
                {uniqueDepartments.map(dept => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div
              style={{
                minWidth: "180px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{ color: "white", fontSize: "13px", direction: dir }}
              >
                {t("reports.payroll.table.status")}
              </label>
              <select
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  direction: dir,
                }}
              >
                <option value="all">
                  {t("common.all", { defaultValue: isRTL ? "الكل" : "All" })}
                </option>
                <option value="generated">
                  {t("reports.payroll.status.generated")}
                </option>
                <option value="draft">
                  {t("reports.payroll.status.draft", {
                    defaultValue: isRTL ? "مسودة" : "Draft",
                  })}
                </option>
              </select>
            </div>

            {/* Sorting Field */}
            <div
              style={{
                minWidth: "180px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <label
                style={{ color: "white", fontSize: "13px", direction: dir }}
              >
                {t("common.sortBy", {
                  defaultValue: isRTL ? "ترتيب حسب" : "Sort by",
                })}
              </label>
              <select
                value={sortField}
                onChange={e => setSortField(e.target.value)}
                style={{
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  direction: dir,
                }}
              >
                <option value="employee_name">
                  {t("reports.payroll.table.employee")}
                </option>
                <option value="department">
                  {t("reports.payroll.table.department")}
                </option>
                <option value="base_salary">
                  {t("reports.payroll.table.baseSalary")}
                </option>
                <option value="total_allowances">
                  {t("reports.payroll.table.allowances")}
                </option>
                <option value="total_deductions">
                  {t("reports.payroll.table.deductions")}
                </option>
                <option value="net_salary">
                  {t("reports.payroll.table.netSalary")}
                </option>
                <option value="status">
                  {t("reports.payroll.table.status")}
                </option>
              </select>
            </div>

            {/* Sort Order */}
            <SortOrderSelect
              value={sortOrder}
              onChange={setSortOrder}
              style={{ minWidth: "150px" }}
              selectStyle={{ padding: "12px 15px", height: "45px" }}
            />

            {/* Reset Button */}
            <button
              onClick={() => {
                setSearchTerm("");
                setDeptFilter("all");
                setStatusFilter("all");
                setSortField("employee_name");
                setSortOrder("asc");
                setCurrentPage(1);
              }}
              style={{
                backgroundColor: "#dc3545",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                height: "45px",
                whiteSpace: "nowrap",
              }}
            >
              {t("common.resetFilters", {
                defaultValue: isRTL ? "مسح الفلاتر" : "Clear Filters",
              })}
            </button>
          </div>

          <div style={{ overflow: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                direction: dir,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #AC2000" }}>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "60px",
                    }}
                  >
                    {t("reports.payroll.table.number")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "150px",
                    }}
                  >
                    {t("reports.payroll.table.employee")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "200px",
                    }}
                  >
                    {t("reports.payroll.table.department")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "150px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("reports.payroll.table.baseSalary")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "130px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("reports.payroll.table.allowances")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "130px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("reports.payroll.table.deductions")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "140px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("reports.payroll.table.netSalary")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "140px",
                    }}
                  >
                    {t("reports.payroll.table.status")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "120px",
                    }}
                  >
                    {t("reports.payroll.table.actions", {
                      defaultValue: isRTL ? "الإجراءات" : "Actions",
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map((item, index) => (
                  <tr
                    key={item.employee_id || index}
                    style={{ borderBottom: "1px solid #333" }}
                  >
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                      }}
                    >
                      {filteredAndSortedReports.indexOf(item) + 1}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        direction: dir,
                      }}
                    >
                      {item.employee_name ||
                        (item.employee
                          ? (isRTL
                              ? item.employee.name_ar
                              : item.employee.name_en) || item.employee.name
                          : "-")}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        whiteSpace: "normal",
                        lineHeight: "1.5",
                        direction: dir,
                      }}
                    >
                      {item.department ||
                        (item.employee?.department
                          ? (isRTL
                              ? item.employee.department.name_ar
                              : item.employee.department.name_en) ||
                            item.employee.department.name
                          : "-")}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        whiteSpace: "nowrap",
                        direction: dir,
                      }}
                    >
                      {formatNumberEnDigits(item.base_salary)}{" "}
                      {t("reports.payroll.currency")}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        whiteSpace: "nowrap",
                        direction: dir,
                      }}
                    >
                      {formatNumberEnDigits(item.total_allowances)}{" "}
                      {t("reports.payroll.currency")}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        whiteSpace: "nowrap",
                        direction: dir,
                      }}
                    >
                      {formatNumberEnDigits(item.total_deductions)}{" "}
                      {t("reports.payroll.currency")}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        direction: dir,
                      }}
                    >
                      {formatNumberEnDigits(item.net_salary)}{" "}
                      {t("reports.payroll.currency")}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                      }}
                    >
                      <span
                        style={{
                          padding: "6px 18px",
                          borderRadius: "20px",
                          backgroundColor:
                            item.status === "generated"
                              ? "rgba(172, 32, 0, 0.2)"
                              : "rgba(220, 53, 69, 0.2)",
                          color: "white",
                          fontSize: "13px",
                          fontWeight: "bold",
                          display: "inline-block",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.status === "generated"
                          ? t("reports.payroll.status.generated")
                          : item.status || t("reports.payroll.status.unknown")}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <button
                          onClick={() =>
                            handleExportSingleEmployeeToExcel(item)
                          }
                          title={t("reports.payroll.actions.exportExcel", {
                            defaultValue: "تصدير Excel",
                          })}
                          style={{
                            backgroundColor: "#27ae60",
                            color: "white",
                            border: "none",
                            padding: "8px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaFileExcel />
                        </button>
                        <button
                          onClick={() => handlePrintSingleEmployeePdf(item)}
                          disabled={isGeneratingSinglePdf}
                          title={t("reports.payroll.actions.printPdf", {
                            defaultValue: "طباعة PDF",
                          })}
                          style={{
                            backgroundColor: "#2980b9",
                            color: "white",
                            border: "none",
                            padding: "8px",
                            borderRadius: "6px",
                            cursor: isGeneratingSinglePdf
                              ? "not-allowed"
                              : "pointer",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: isGeneratingSinglePdf ? 0.6 : 1,
                          }}
                        >
                          <FaPrint />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                marginTop: "20px",
                paddingBottom: "20px",
                direction: dir,
              }}
            >
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                disabled={currentPageSafe === 1}
                style={{
                  padding: "8px 12px",
                  backgroundColor: currentPageSafe === 1 ? "#333" : "#AC2000",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: currentPageSafe === 1 ? "not-allowed" : "pointer",
                }}
              >
                {t("common.previous", {
                  defaultValue: isRTL ? "السابق" : "Previous",
                })}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  page =>
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPageSafe - 2 &&
                      page <= currentPageSafe + 2),
                )
                .map((page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) {
                    return (
                      <React.Fragment key={`ellipsis-${page}`}>
                        <span style={{ padding: "8px", color: "white" }}>
                          ...
                        </span>
                        <button
                          onClick={() => setCurrentPage(page)}
                          style={{
                            padding: "8px 12px",
                            backgroundColor:
                              page === currentPageSafe ? "#AC2000" : "#1a1f2e",
                            color: "white",
                            border: "1px solid #333",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        padding: "8px 12px",
                        backgroundColor:
                          page === currentPageSafe ? "#AC2000" : "#1a1f2e",
                        color: "white",
                        border: "1px solid #333",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                }
                disabled={currentPageSafe === totalPages}
                style={{
                  padding: "8px 12px",
                  backgroundColor:
                    currentPageSafe === totalPages ? "#333" : "#AC2000",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    currentPageSafe === totalPages ? "not-allowed" : "pointer",
                }}
              >
                {t("common.next", { defaultValue: isRTL ? "التالي" : "Next" })}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {payrollReports && (!allReports || allReports.length === 0) && (
        <div
          style={{
            backgroundColor: "#1b3350",
            borderRadius: "12px",
            padding: "40px 20px",
            border: "1px solid #333",
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: "#999",
              fontSize: "18px",
              fontWeight: "500",
            }}
          >
            {t("reports.payroll.noData")}
          </div>
        </div>
      )}

      {/* PDF Measurement Element - Hidden offscreen */}
      {filteredAndSortedReports && filteredAndSortedReports.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "-99999px",
            left: "-99999px",
            width: "794px",
          }}
        >
          <div
            style={{
              width: "794px",
              backgroundColor: "#ffffff",
              color: "#000000",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              padding: "40px",
              boxSizing: "border-box",
              direction: dir,
            }}
          >
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
              }}
            >
              <div
                style={{
                  border: "2px solid #000",
                  padding: "18px 18px 10px",
                  display: "flex",
                  flexDirection: "column",
                  direction: dir,
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <div
                  id="pdf-measure-top-header"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                  <div style={{ textAlign: "center", marginBottom: "16px" }}>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#0d2a50",
                      }}
                    >
                      {t("reports.payroll.print.title", {
                        defaultValue: "تقرير مسير الرواتب الشهري",
                      })}
                    </div>
                    <div style={{ fontSize: "14px", marginTop: "4px" }}>
                      {formatReportMonthYear()}
                    </div>
                    {/* Applied Filters info */}
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#333",
                        marginTop: "4px",
                        display: "flex",
                        justifyContent: "center",
                        gap: "15px",
                      }}
                    >
                      {searchTerm.trim() && (
                        <span>
                          {t("common.search", { defaultValue: "بحث" })}:{" "}
                          {searchTerm}
                        </span>
                      )}
                      {deptFilter !== "all" && (
                        <span>
                          {t("reports.payroll.table.department")}: {deptFilter}
                        </span>
                      )}
                      {statusFilter !== "all" && (
                        <span>
                          {t("reports.payroll.table.status")}: {statusFilter}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#555",
                        marginTop: "2px",
                      }}
                    >
                      {t("reports.payroll.print.employeesCount", {
                        defaultValue: isRTL
                          ? "عدد الموظفين"
                          : "Number of Employees",
                      })}
                      : {filteredAndSortedReports.length}
                    </div>
                  </div>
                </div>

                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "10px",
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: "6%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "15%" }} />
                  </colgroup>
                  <thead id="pdf-measure-thead">
                    <tr
                      style={{ backgroundColor: "#e3f2fd", color: "#0d2a50" }}
                    >
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 3px",
                          textAlign: "center",
                        }}
                      >
                        {t("reports.payroll.print.serial", {
                          defaultValue: isRTL ? "م" : "Serial",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 3px",
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {t("reports.payroll.print.employee", {
                          defaultValue: isRTL ? "الموظف" : "Employee",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 3px",
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {t("reports.payroll.print.department", {
                          defaultValue: isRTL ? "القسم" : "Department",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 3px",
                          textAlign: "center",
                        }}
                      >
                        {t("reports.payroll.print.baseSalary", {
                          defaultValue: isRTL
                            ? "الراتب الأساسي"
                            : "Base Salary",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 3px",
                          textAlign: "center",
                        }}
                      >
                        {t("reports.payroll.print.allowances", {
                          defaultValue: isRTL ? "البدلات" : "Allowances",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 3px",
                          textAlign: "center",
                        }}
                      >
                        {t("reports.payroll.print.deductions", {
                          defaultValue: isRTL ? "الخصومات" : "Deductions",
                        })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "4px 3px",
                          textAlign: "center",
                        }}
                      >
                        {t("reports.payroll.print.netSalary", {
                          defaultValue: isRTL ? "الراتب الصافي" : "Net Salary",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody id="pdf-measure-tbody">
                    {filteredAndSortedReports.map((item, index) => {
                      const cell = {
                        border: "1px solid #000",
                        padding: "3px 2px",
                        fontSize: "9px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      };
                      return (
                        <tr key={item.employee_id || index}>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {index + 1}
                          </td>
                          <td
                            style={{
                              ...cell,
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {item.employee_name ||
                              (item.employee
                                ? (isRTL
                                    ? item.employee.name_ar
                                    : item.employee.name_en) ||
                                  item.employee.name
                                : "—")}
                          </td>
                          <td
                            style={{
                              ...cell,
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {item.department ||
                              (item.employee?.department
                                ? (isRTL
                                    ? item.employee.department.name_ar
                                    : item.employee.department.name_en) ||
                                  item.employee.department.name
                                : "—")}
                          </td>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {formatNumberEnDigits(item.base_salary || 0)}
                          </td>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {formatNumberEnDigits(item.total_allowances || 0)}
                          </td>
                          <td style={{ ...cell, textAlign: "center" }}>
                            {formatNumberEnDigits(item.total_deductions || 0)}
                          </td>
                          <td
                            style={{
                              ...cell,
                              textAlign: "center",
                              fontWeight: "700",
                              color: "#AC2000",
                            }}
                          >
                            {formatNumberEnDigits(item.net_salary || 0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div
                  id="pdf-measure-footer"
                  style={{ marginTop: "auto", paddingTop: "6px" }}
                >
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Template - Hidden (For actual printing) */}
      {pdfChunks && pdfChunks.length > 0 && (
        <div
          style={{ position: "absolute", top: "-99999px", left: "-99999px" }}
        >
          {pdfChunks.map((chunk, chunkIdx, allChunks) => (
            <div
              key={chunkIdx}
              id={`payroll-report-pdf-chunk-${chunkIdx}`}
              style={{
                width: "794px",
                height: "1123px",
                backgroundColor: "#ffffff",
                color: "#000000",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                padding: "40px",
                boxSizing: "border-box",
                direction: dir,
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    border: "2px solid #000",
                    height: "100%",
                    padding: "18px 18px 10px",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    textRendering: "optimizeLegibility",
                    direction: dir,
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      height: "600px",
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: "95%",
                      backgroundPosition: "center center",
                      backgroundRepeat: "no-repeat",
                      opacity: 0.1,
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        marginBottom: "8px",
                        borderBottom: "4px double #000",
                        paddingBottom: "8px",
                        flexShrink: 0,
                      }}
                      crossOrigin="anonymous"
                    />
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        minHeight: 0,
                      }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          marginBottom: "16px",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "#0d2a50",
                            direction: dir,
                          }}
                        >
                          {t("reports.payroll.print.title", {
                            defaultValue: "تقرير مسير الرواتب الشهري",
                          })}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            marginTop: "4px",
                            direction: dir,
                          }}
                        >
                          {formatReportMonthYear()}
                        </div>
                        {/* Applied Filters info */}
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#333",
                            marginTop: "4px",
                            display: "flex",
                            justifyContent: "center",
                            gap: "15px",
                            direction: dir,
                          }}
                        >
                          {searchTerm.trim() && (
                            <span>
                              {t("common.search", { defaultValue: "بحث" })}:{" "}
                              {searchTerm}
                            </span>
                          )}
                          {deptFilter !== "all" && (
                            <span>
                              {t("reports.payroll.table.department")}:{" "}
                              {deptFilter}
                            </span>
                          )}
                          {statusFilter !== "all" && (
                            <span>
                              {t("reports.payroll.table.status")}:{" "}
                              {statusFilter}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#555",
                            marginTop: "2px",
                            direction: dir,
                          }}
                        >
                          {t("reports.payroll.print.employeesCount", {
                            defaultValue: isRTL
                              ? "عدد الموظفين"
                              : "Number of Employees",
                          })}
                          : {filteredAndSortedReports.length}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "10px",
                            direction: dir,
                            tableLayout: "fixed",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "6%" }} />
                            <col style={{ width: "22%" }} />
                            <col style={{ width: "18%" }} />
                            <col style={{ width: "13%" }} />
                            <col style={{ width: "13%" }} />
                            <col style={{ width: "13%" }} />
                            <col style={{ width: "15%" }} />
                          </colgroup>
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#e3f2fd",
                                color: "#0d2a50",
                              }}
                            >
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 3px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("reports.payroll.print.serial", {
                                  defaultValue: isRTL ? "م" : "Serial",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 3px",
                                  direction: dir,
                                  textAlign: isRTL ? "right" : "left",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("reports.payroll.print.employee", {
                                  defaultValue: isRTL ? "الموظف" : "Employee",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 3px",
                                  direction: dir,
                                  textAlign: isRTL ? "right" : "left",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("reports.payroll.print.department", {
                                  defaultValue: isRTL ? "القسم" : "Department",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 3px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("reports.payroll.print.baseSalary", {
                                  defaultValue: isRTL
                                    ? "الراتب الأساسي"
                                    : "Base Salary",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 3px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("reports.payroll.print.allowances", {
                                  defaultValue: isRTL
                                    ? "البدلات"
                                    : "Allowances",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 3px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("reports.payroll.print.deductions", {
                                  defaultValue: isRTL
                                    ? "الخصومات"
                                    : "Deductions",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 3px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("reports.payroll.print.netSalary", {
                                  defaultValue: isRTL
                                    ? "الراتب الصافي"
                                    : "Net Salary",
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.map((item, index) => {
                              const cell = {
                                border: "1px solid #000",
                                padding: "3px 2px",
                                fontSize: "9px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              };
                              return (
                                <tr key={item.employee_id || index}>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {filteredAndSortedReports.indexOf(item) + 1}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      direction: dir,
                                      textAlign: isRTL ? "right" : "left",
                                    }}
                                  >
                                    {item.employee_name ||
                                      (item.employee
                                        ? (isRTL
                                            ? item.employee.name_ar
                                            : item.employee.name_en) ||
                                          item.employee.name
                                        : "—")}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      direction: dir,
                                      textAlign: isRTL ? "right" : "left",
                                    }}
                                  >
                                    {item.department ||
                                      (item.employee?.department
                                        ? (isRTL
                                            ? item.employee.department.name_ar
                                            : item.employee.department
                                                .name_en) ||
                                          item.employee.department.name
                                        : "—")}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {formatNumberEnDigits(
                                      item.base_salary || 0,
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {formatNumberEnDigits(
                                      item.total_allowances || 0,
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {formatNumberEnDigits(
                                      item.total_deductions || 0,
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      fontWeight: "700",
                                      color: "#AC2000",
                                      direction: dir,
                                    }}
                                  >
                                    {formatNumberEnDigits(item.net_salary || 0)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {chunkIdx === allChunks.length - 1 && (
                        <div
                          style={{
                            display: "flex",
                            gap: "16px",
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#0d2a50",
                            marginBottom: "20px",
                            direction: dir,
                            flexShrink: 0,
                            marginTop: "10px",
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              border: "1px solid #000",
                              padding: "10px",
                              textAlign: "center",
                              direction: dir,
                            }}
                          >
                            {t("reports.payroll.print.totalNetSalaries", {
                              defaultValue: "إجمالي الرواتب الصافية",
                            })}
                            <br />
                            <span
                              style={{ fontSize: "16px", color: "#AC2000" }}
                            >
                              {formatPrintCurrency(
                                filteredAndSortedReports.reduce(
                                  (sum, item) =>
                                    sum + parseFloat(item.net_salary || 0),
                                  0,
                                ),
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                      <div
                        style={{
                          flex: 1,
                          borderLeft: "1px solid #000",
                          borderRight: "1px solid #000",
                          borderBottom: "1px solid #000",
                          backgroundColor: "rgba(255,255,255,0.7)",
                        }}
                      />
                    </div>
                    <div style={{ flexShrink: 0, paddingTop: "6px" }}>
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                        }}
                        crossOrigin="anonymous"
                      />
                      <div
                        style={{
                          textAlign: "center",
                          marginTop: "4px",
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        <div
                          style={{
                            fontStyle: "italic",
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: "6px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            direction: "ltr",
                          }}
                        >
                          <span>Page</span>
                          <strong style={{ fontSize: "13px" }}>
                            {chunkIdx + 1} / {allChunks.length}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={
          isPreviewOpen &&
          filteredAndSortedReports &&
          filteredAndSortedReports.length > 0
        }
        title={t("reports.payroll.title")}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintPdf}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          id="preview-payroll-report-pdf"
          style={{
            width: "794px",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily: '"Times New Roman", "Times", serif',
            boxSizing: "border-box",
            direction: dir,
            transform: "scale(1.0)",
            transformOrigin: "top center",
          }}
        >
          {pdfChunks &&
            pdfChunks.map((chunk, chunkIdx, allChunks) => (
              <div
                key={chunkIdx}
                style={{
                  width: "794px",
                  height: "1123px",
                  padding: "40px",
                  boxSizing: "border-box",
                  backgroundColor: "#ffffff",
                  marginBottom: chunkIdx < allChunks.length - 1 ? "20px" : "0",
                  margin: "0 auto",
                }}
              >
                <div
                  style={{
                    border: "5px solid #000",
                    padding: "3px",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      border: "2px solid #000",
                      height: "100%",
                      padding: "18px 18px 10px",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      WebkitFontSmoothing: "antialiased",
                      MozOsxFontSmoothing: "grayscale",
                      textRendering: "optimizeLegibility",
                      direction: dir,
                      backgroundColor: "#ffffff",
                      boxSizing: "border-box",
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "100%",
                        height: "600px",
                        backgroundImage: `url(${headerLogo})`,
                        backgroundSize: "95%",
                        backgroundPosition: "center center",
                        backgroundRepeat: "no-repeat",
                        opacity: 0.1,
                        zIndex: 0,
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      style={{
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      <img
                        src={`${publicUrl}/HeaderAce.png`}
                        alt="Header"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                          marginBottom: "8px",
                          borderBottom: "4px double #000",
                          paddingBottom: "8px",
                          flexShrink: 0,
                        }}
                        crossOrigin="anonymous"
                      />
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                          minHeight: 0,
                        }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            marginBottom: "16px",
                            flexShrink: 0,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#0d2a50",
                              direction: dir,
                            }}
                          >
                            {t("reports.payroll.print.title", {
                              defaultValue: "تقرير مسير الرواتب الشهري",
                            })}
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              marginTop: "4px",
                              direction: dir,
                            }}
                          >
                            {formatReportMonthYear()}
                          </div>
                          {/* Applied Filters info */}
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#333",
                              marginTop: "4px",
                              display: "flex",
                              justifyContent: "center",
                              gap: "15px",
                              direction: dir,
                            }}
                          >
                            {searchTerm.trim() && (
                              <span>
                                {t("common.search", { defaultValue: "بحث" })}:{" "}
                                {searchTerm}
                              </span>
                            )}
                            {deptFilter !== "all" && (
                              <span>
                                {t("reports.payroll.table.department")}:{" "}
                                {deptFilter}
                              </span>
                            )}
                            {statusFilter !== "all" && (
                              <span>
                                {t("reports.payroll.table.status")}:{" "}
                                {statusFilter}
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#555",
                              marginTop: "2px",
                              direction: dir,
                            }}
                          >
                            {t("reports.payroll.print.employeesCount", {
                              defaultValue: isRTL
                                ? "عدد الموظفين"
                                : "Number of Employees",
                            })}
                            : {filteredAndSortedReports.length}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              fontSize: "10px",
                              direction: dir,
                              tableLayout: "fixed",
                            }}
                          >
                            <colgroup>
                              <col style={{ width: "6%" }} />
                              <col style={{ width: "22%" }} />
                              <col style={{ width: "18%" }} />
                              <col style={{ width: "13%" }} />
                              <col style={{ width: "13%" }} />
                              <col style={{ width: "13%" }} />
                              <col style={{ width: "15%" }} />
                            </colgroup>
                            <thead>
                              <tr
                                style={{
                                  backgroundColor: "#e3f2fd",
                                  color: "#0d2a50",
                                }}
                              >
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 3px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("reports.payroll.print.serial", {
                                    defaultValue: isRTL ? "م" : "Serial",
                                  })}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 3px",
                                    direction: dir,
                                    textAlign: isRTL ? "right" : "left",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("reports.payroll.print.employee", {
                                    defaultValue: isRTL ? "الموظف" : "Employee",
                                  })}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 3px",
                                    direction: dir,
                                    textAlign: isRTL ? "right" : "left",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("reports.payroll.print.department", {
                                    defaultValue: isRTL
                                      ? "القسم"
                                      : "Department",
                                  })}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 3px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("reports.payroll.print.baseSalary", {
                                    defaultValue: isRTL
                                      ? "الراتب الأساسي"
                                      : "Base Salary",
                                  })}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 3px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("reports.payroll.print.allowances", {
                                    defaultValue: isRTL
                                      ? "البدلات"
                                      : "Allowances",
                                  })}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 3px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("reports.payroll.print.deductions", {
                                    defaultValue: isRTL
                                      ? "الخصومات"
                                      : "Deductions",
                                  })}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 3px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("reports.payroll.print.netSalary", {
                                    defaultValue: isRTL
                                      ? "الراتب الصافي"
                                      : "Net Salary",
                                  })}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {chunk.map((item, index) => {
                                const cell = {
                                  border: "1px solid #000",
                                  padding: "3px 2px",
                                  fontSize: "9px",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                };
                                return (
                                  <tr key={item.employee_id || index}>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {filteredAndSortedReports.indexOf(item) +
                                        1}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        direction: dir,
                                        textAlign: isRTL ? "right" : "left",
                                      }}
                                    >
                                      {item.employee_name ||
                                        (item.employee
                                          ? (isRTL
                                              ? item.employee.name_ar
                                              : item.employee.name_en) ||
                                            item.employee.name
                                          : "—")}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        direction: dir,
                                        textAlign: isRTL ? "right" : "left",
                                      }}
                                    >
                                      {item.department ||
                                        (item.employee?.department
                                          ? (isRTL
                                              ? item.employee.department.name_ar
                                              : item.employee.department
                                                  .name_en) ||
                                            item.employee.department.name
                                          : "—")}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {formatNumberEnDigits(
                                        item.base_salary || 0,
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {formatNumberEnDigits(
                                        item.total_allowances || 0,
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {formatNumberEnDigits(
                                        item.total_deductions || 0,
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        fontWeight: "700",
                                        color: "#AC2000",
                                        direction: dir,
                                      }}
                                    >
                                      {formatNumberEnDigits(
                                        item.net_salary || 0,
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {chunkIdx === allChunks.length - 1 && (
                          <div
                            style={{
                              display: "flex",
                              gap: "16px",
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#0d2a50",
                              marginBottom: "20px",
                              direction: dir,
                              flexShrink: 0,
                              marginTop: "10px",
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                border: "1px solid #000",
                                padding: "10px",
                                textAlign: "center",
                                direction: dir,
                              }}
                            >
                              {t("reports.payroll.print.totalNetSalaries", {
                                defaultValue: "إجمالي الرواتب الصافية",
                              })}
                              <br />
                              <span
                                style={{ fontSize: "16px", color: "#AC2000" }}
                              >
                                {formatPrintCurrency(
                                  filteredAndSortedReports.reduce(
                                    (sum, item) =>
                                      sum + parseFloat(item.net_salary || 0),
                                    0,
                                  ),
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                        <div
                          style={{
                            flex: 1,
                            borderLeft: "1px solid #000",
                            borderRight: "1px solid #000",
                            borderBottom: "1px solid #000",
                            backgroundColor: "rgba(255,255,255,0.7)",
                          }}
                        />
                      </div>
                      <div style={{ flexShrink: 0, paddingTop: "6px" }}>
                        <img
                          src={`${publicUrl}/FooterAce.png`}
                          alt="Footer"
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                          }}
                          crossOrigin="anonymous"
                        />
                        <div
                          style={{
                            textAlign: "center",
                            marginTop: "4px",
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                          <div
                            style={{
                              fontStyle: "italic",
                              display: "inline-flex",
                              alignItems: "baseline",
                              gap: "6px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              direction: "ltr",
                            }}
                          >
                            <span>Page</span>
                            <strong style={{ fontSize: "13px" }}>
                              {chunkIdx + 1} / {allChunks.length}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </PdfPreviewModal>

      {/* Single Employee PDF Template - Hidden */}
      {selectedEmployee && (
        <div
          style={{ position: "absolute", top: "-99999px", left: "-99999px" }}
        >
          <div
            id="single-employee-payroll-pdf"
            ref={singleEmployeePdfRef}
            style={{
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              color: "#000000",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              padding: "40px",
              boxSizing: "border-box",
              direction: dir,
            }}
          >
            {/* Outer thick border container */}
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                height: "1043px",
                overflow: "visible",
              }}
            >
              {/* Inner border container */}
              <div
                style={{
                  border: "2px solid #000",
                  height: "1033px",
                  padding: "18px 18px 10px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  direction: dir,
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  overflow: "visible",
                }}
              >
                {/* Background Logo */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "100%",
                    height: "600px",
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: "95%",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    opacity: 0.1,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "100%",
                  }}
                >
                  {/* Header */}
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                      flexShrink: 0,
                    }}
                    crossOrigin="anonymous"
                  />
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div style={{ textAlign: "center", marginBottom: "16px" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#0d2a50",
                          direction: dir,
                        }}
                      >
                        {t("reports.payroll.print.singleEmployee.title", {
                          defaultValue: "تقرير راتب موظف",
                        })}
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          marginTop: "4px",
                          direction: dir,
                        }}
                      >
                        {formatReportMonthYear()}
                      </div>
                    </div>

                    {/* Employee Information */}
                    <div
                      style={{
                        border: "1px solid #000",
                        padding: "12px",
                        marginBottom: "16px",
                        direction: dir,
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(200px, 1fr))",
                          gap: "10px",
                          fontSize: "13px",
                          lineHeight: 1.6,
                          direction: dir,
                          textAlign,
                        }}
                      >
                        <div>
                          <strong>
                            {t("reports.payroll.print.employee", {
                              defaultValue: "الموظف",
                            })}
                            :
                          </strong>{" "}
                          {selectedEmployee.employee_name ||
                            (selectedEmployee.employee
                              ? (isRTL
                                  ? selectedEmployee.employee.name_ar
                                  : selectedEmployee.employee.name_en) ||
                                selectedEmployee.employee.name
                              : "—")}
                        </div>
                        <div>
                          <strong>
                            {t("reports.payroll.print.department", {
                              defaultValue: "القسم",
                            })}
                            :
                          </strong>{" "}
                          {selectedEmployee.department ||
                            (selectedEmployee.employee?.department
                              ? (isRTL
                                  ? selectedEmployee.employee.department.name_ar
                                  : selectedEmployee.employee.department
                                      .name_en) ||
                                selectedEmployee.employee.department.name
                              : "—")}
                        </div>
                      </div>
                    </div>

                    {/* Salary Details Table */}
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "12px",
                        marginBottom: "18px",
                        direction: dir,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "#e3f2fd",
                            color: "#0d2a50",
                          }}
                        >
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              direction: dir,
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {t("reports.payroll.print.singleEmployee.item", {
                              defaultValue: "البند",
                            })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              direction: dir,
                              textAlign: "center",
                            }}
                          >
                            {t("reports.payroll.print.singleEmployee.amount", {
                              defaultValue: "المبلغ",
                            })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              direction: dir,
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {t("reports.payroll.print.baseSalary", {
                              defaultValue: "الراتب الأساسي",
                            })}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              direction: dir,
                            }}
                          >
                            {formatPrintCurrency(selectedEmployee.base_salary)}
                          </td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              direction: dir,
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {t("reports.payroll.print.allowances", {
                              defaultValue: "البدلات",
                            })}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              color: "#1976d2",
                              direction: dir,
                            }}
                          >
                            +
                            {formatPrintCurrency(
                              selectedEmployee.total_allowances,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              direction: dir,
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {t("reports.payroll.print.deductions", {
                              defaultValue: "الخصومات",
                            })}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              color: "#dc3545",
                              direction: dir,
                            }}
                          >
                            -
                            {formatPrintCurrency(
                              selectedEmployee.total_deductions,
                            )}
                          </td>
                        </tr>
                        <tr style={{ backgroundColor: "#f0f0f0" }}>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              fontWeight: "700",
                              direction: dir,
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {t("reports.payroll.print.netSalary", {
                              defaultValue: "الراتب الصافي",
                            })}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              color: "#AC2000",
                              fontSize: "14px",
                              direction: dir,
                            }}
                          >
                            {formatPrintCurrency(selectedEmployee.net_salary)}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        fontSize: "13px",
                        fontWeight: "700",
                        color: "#0d2a50",
                        marginBottom: "20px",
                        direction: dir,
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          border: "1px solid #000",
                          padding: "10px",
                          textAlign: "center",
                          direction: dir,
                        }}
                      >
                        {t("reports.payroll.print.singleEmployee.status", {
                          defaultValue: "الحالة",
                        })}
                        :{" "}
                        {selectedEmployee.status === "generated"
                          ? t("reports.payroll.print.generated", {
                              defaultValue: "تم الإنشاء",
                            })
                          : selectedEmployee.status || "—"}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div
                    style={{
                      marginTop: "auto",
                      flexShrink: 0,
                      paddingTop: "20px",
                    }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                      }}
                      crossOrigin="anonymous"
                    />
                    <div
                      style={{
                        textAlign: "center",
                        marginTop: "6px",
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                    >
                      <div
                        style={{
                          fontStyle: "italic",
                          marginTop: "2px",
                          display: "inline-flex",
                          alignItems: "baseline",
                          gap: "6px",
                          fontSize: "12px",
                          fontFamily:
                            '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          fontWeight: "bold",
                          direction: "ltr",
                        }}
                      >
                        <span
                          style={{
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                          Page
                        </span>
                        <strong
                          style={{
                            fontSize: "13px",
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                          1 / 1
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollReport;
